import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";

/**
 * Página de espera de verificación de email.
 * Estados:
 *  - default: muestra info + botón "Reenviar" + link "Cambiar email"
 *  - resending: spinner
 *  - resent: confirmación visual
 *  - editing-email: form para cambiar email pendiente (requiere password)
 *  - resend-cooldown: muestra timer
 */
export default function VerifyEmailPendingPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const initialEmail = location.state?.email || "";
  const [email, setEmail] = useState(initialEmail);
  const [cooldown, setCooldown] = useState(0); // segundos restantes
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [lastSentAt, setLastSentAt] = useState(null);

  // Si no hay email en state, redirige a login.
  useEffect(() => {
    if (!initialEmail) {
      navigate("/login", { replace: true });
    }
  }, [initialEmail, navigate]);

  // Cooldown timer.
  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setInterval(() => setCooldown((c) => Math.max(0, c - 1)), 1000);
    return () => clearInterval(t);
  }, [cooldown]);

  const resend = async () => {
    setError("");
    setSending(true);
    try {
      await api.post("/auth/resend-verification", { email });
      toast.success("Si tu cuenta está pendiente, recibirás un nuevo enlace en breve.");
      setLastSentAt(new Date());
      setCooldown(60);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 429) {
        const retryAfter = parseInt(e?.response?.headers?.["retry-after"] || "60", 10);
        setCooldown(retryAfter);
        setError(`Espera ${retryAfter}s antes de solicitar otro enlace.`);
      } else {
        setError(formatApiError(e?.response?.data?.detail) || "No pudimos reenviar el enlace.");
      }
    } finally {
      setSending(false);
    }
  };

  const changeEmail = async (e) => {
    e.preventDefault();
    setError("");
    if (!newEmail.trim() || !password) {
      setError("Completa todos los campos.");
      return;
    }
    setSending(true);
    try {
      await api.post("/auth/change-pending-email", {
        current_email: email,
        new_email: newEmail.trim(),
        password,
      });
      toast.success("Email actualizado. Revisa tu nueva bandeja de entrada.");
      setEmail(newEmail.trim());
      setEditing(false);
      setNewEmail("");
      setPassword("");
      setLastSentAt(new Date());
      setCooldown(60);
    } catch (err) {
      setError(formatApiError(err?.response?.data?.detail) || "No se pudo cambiar el email.");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container-prose py-16" data-testid="verify-email-pending-page">
      <div className="max-w-lg mx-auto">
        <div className="flex justify-center mb-6">
          <Link to="/" aria-label="JustSapiens Procure">
            <img src="/brand/logo-stacked-positive.png" alt="JustSapiens Procure" className="h-24 w-auto object-contain" />
          </Link>
        </div>

        <div className="border border-gray-200 bg-white p-8">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-50 border border-amber-200 mb-4">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-8 w-8 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
              </svg>
            </div>
            <p className="text-xs uppercase tracking-[0.22em] text-primary font-medium mb-2">Verificación pendiente</p>
            <h1 className="font-serif text-2xl text-gray-900 mb-3">Revisa tu correo</h1>
            <p className="text-sm text-gray-600 leading-relaxed">
              Te enviamos un enlace de confirmación a{" "}
              <span className="font-mono text-gray-900" data-testid="pending-email">{email}</span>.
              Haz clic en el botón del email para activar tu cuenta.
            </p>
            {lastSentAt && (
              <p className="text-xs text-gray-500 mt-2">
                Último envío: {lastSentAt.toLocaleTimeString("es-CO")}
              </p>
            )}
          </div>

          {!editing ? (
            <div className="space-y-3">
              <div className="border border-gray-100 bg-gray-50 p-4 text-xs text-gray-600 leading-relaxed">
                <strong>¿No lo encuentras?</strong> Revisa también tu carpeta de spam o promociones. El enlace es válido por <strong>24 horas</strong>.
              </div>

              {error && (
                <div data-testid="resend-error" className="text-sm text-rose-700 border border-rose-200 bg-rose-50 px-3 py-2">
                  {error}
                </div>
              )}

              <Button
                onClick={resend}
                disabled={sending || cooldown > 0}
                className="w-full rounded-sm h-11"
                data-testid="resend-verification-btn"
              >
                {sending
                  ? "Enviando…"
                  : cooldown > 0
                    ? `Reenviar en ${cooldown}s`
                    : "Reenviar enlace"}
              </Button>
              <button
                type="button"
                onClick={() => { setEditing(true); setNewEmail(""); setError(""); }}
                className="w-full text-sm text-primary hover:underline"
                data-testid="change-email-btn"
              >
                ¿Te equivocaste? Cambiar mi email
              </button>
              <p className="text-xs text-gray-500 text-center pt-2">
                <Link to="/login" className="hover:text-primary">← Volver a iniciar sesión</Link>
              </p>
            </div>
          ) : (
            <form onSubmit={changeEmail} className="space-y-3" data-testid="change-email-form">
              <p className="text-sm text-gray-700 mb-2">
                Ingresa tu nuevo email y confirma con tu contraseña.
              </p>
              <div className="space-y-1.5">
                <Label htmlFor="new-email">Nuevo email</Label>
                <Input
                  id="new-email"
                  type="email"
                  required
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  className="rounded-sm h-11"
                  data-testid="new-email-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="confirm-pwd">Contraseña</Label>
                <Input
                  id="confirm-pwd"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="rounded-sm h-11"
                  data-testid="change-email-password-input"
                />
              </div>
              {error && (
                <div data-testid="change-email-error" className="text-sm text-rose-700 border border-rose-200 bg-rose-50 px-3 py-2">
                  {error}
                </div>
              )}
              <Button type="submit" disabled={sending} className="w-full rounded-sm h-11" data-testid="change-email-submit-btn">
                {sending ? "Actualizando…" : "Actualizar email"}
              </Button>
              <button
                type="button"
                onClick={() => { setEditing(false); setError(""); }}
                className="w-full text-sm text-gray-500 hover:text-primary"
                data-testid="cancel-change-email-btn"
              >
                Cancelar
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
